// src/i18n/index.ts
// i18next bootstrap — imported for side-effect in index.tsx BEFORE App renders.
// RESEARCH Pattern 1 (lines 187-228) + CONTEXT decisions D-01/D-02/D-06/D-09.
//
// Single default namespace ("common") with FLAT keys matching the existing
// strings.ts shape 1:1, so `strings.X -> t('X')` requires no namespace prefix.
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import heCommon from "./locales/he/common.json";
import enCommon from "./locales/en/common.json";

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { common: enCommon },
      he: { common: heCommon },
    },
    fallbackLng: "en", // D-01: hard English default for fresh visits
    supportedLngs: ["en", "he"],
    ns: ["common"],
    defaultNS: "common",
    // Hebrew SOURCE STRINGS are used directly as i18n keys (gap-pass extraction).
    // Many are full sentences containing `.`/`?`/`:` which i18next would otherwise
    // misread as nested-key / namespace paths. Disable both separators.
    // VERIFIED SAFE: zero dotted-key t() calls, zero existing catalog keys with
    // `.`/`:`, zero <Trans i18nKey> usages — does not affect the existing keys.
    keySeparator: false,
    nsSeparator: false,
    detection: {
      order: ["localStorage"], // D-01: NO browser-language detection
      caches: ["localStorage"], // D-02: persist the user's choice
      lookupLocalStorage: "i18nextLng",
    },
    interpolation: { escapeValue: false }, // React escapes JSX — keep for XSS safety (T-01-X)
  });

// D-09: single source of truth for layout direction.
// Keep DOM dir/lang in lockstep with the active language — i18next knows "he" is rtl.
// (`i18n.dir` is typed via the augmentation in ./i18next.d.ts — see that file for
// why TS 4.9.5 needs it; the method is present and correct at runtime.)
const applyDir = (lng: string): void => {
  document.documentElement.dir = i18n.dir(lng);
  document.documentElement.lang = lng;
};

applyDir(i18n.resolvedLanguage ?? "en");
i18n.on("languageChanged", applyDir);

// i18next 26's `.d.ts` uses `const` type-params (TS 5.0+) that this repo's pinned
// TypeScript 4.9.5 mis-parses, corrupting `TFunction` into a 0-arg signature. That
// poisons `i18n.t("key")` at every consumer under the CRA fork-ts-checker build
// (skipLibCheck only skips *checking* the broken `.d.ts`, not *parsing* it — and
// interface-merge augmentation cannot override the existing `t` PROPERTY type).
// So re-type the exported instance: intersect with a clean, permissive `t`/`exists`
// so module-scope `i18n.t("key")` / `i18n.t("key", { count })` call sites typecheck.
// Verified correct at runtime — `t` returns the resolved string.
type I18nWithCleanT = typeof i18n & {
  t: (key: string, options?: Record<string, unknown>) => string;
};

export default i18n as I18nWithCleanT;
